<template>
  <div class="financial-transactions-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-chart-line"></i>
        Histórico de Cobradores
      </h1>
      <p class="page-subtitle">Consulte o histórico e comissões dos cobradores</p>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando dados...</span>
      </div>
    </div>

    <!-- Tab content -->
    <div v-show="!loading" class="tab-content">
      <!-- Filtros -->
      <div class="filters-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-filter"></i>
            Filtros de Consulta
          </h3>
        </div>
        
        <div class="filters-content">
          <div class="filters-grid">
            <!-- Seleção de Cobradores -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-users"></i>
                Cobradores *
              </label>
              <div class="multiselect-container">
                <MultiSelect 
                  v-model="selectedCollectorsObjects" 
                  :options="availableCollectors" 
                  placeholder="Selecione os cobradores"
                  :showSelectAllButtons="true"
                />
              </div>
            </div>

            <!-- Incluir Vendedores Inativos -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-user-slash"></i>
                Incluir Vendedores Inativos
              </label>
              <select v-model="includeInactiveSellers" class="form-input">
                <option value="false">NÃO</option>
                <option value="true">SIM</option>
              </select>
            </div>

            <!-- Comissão Sobre -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-percentage"></i>
                Comissão Sobre
              </label>
              <select v-model="commissionType" class="form-input">
                <option value="BRUTO/LIQUIDO">BRUTO/LÍQUIDO</option>
                <option value="BRUTO">BRUTO</option>
                <option value="LIQUIDO">LÍQUIDO</option>
              </select>
            </div>

            <!-- Líquido de Prêmio -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-trophy"></i>
                Líquido de Prêmio
              </label>
              <select v-model="liquidPrize" class="form-input">
                <option value="SEM DESPESA">SEM DESPESA</option>
                <option value="COM DESPESA">COM DESPESA</option>
              </select>
            </div>

            <!-- Data Inicial -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-calendar-days"></i>
                Data Inicial *
              </label>
              <input 
                type="date" 
                v-model="startDate" 
                class="form-input"
              />
            </div>

            <!-- Data Final -->
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-calendar-days"></i>
                Data Final *
              </label>
              <input 
                type="date" 
                v-model="endDate" 
                class="form-input"
              />
            </div>
          </div>

          <div class="filters-actions">
            <button 
              type="button" 
              class="btn-primary" 
              @click="searchHistory()"
              :disabled="loading"
            >
              <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-search"></i>
              {{ loading ? 'Consultando...' : 'Pesquisar' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Tabela Principal: Histórico Individual dos Vendedores -->
      <div v-show="hasSearched" class="results-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-table"></i>
            Histórico Individual dos Vendedores
          </h3>
          <div class="card-actions">
            <div class="table-zoom-controls" title="Zoom da tabela">
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom <= 5"
                @click="decreaseTableZoom"
                title="Diminuir zoom"
              >
                <i class="fa-solid fa-magnifying-glass-minus"></i>
              </button>
              <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
                {{ tableZoom }}%
              </span>
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom >= 100"
                @click="increaseTableZoom"
                title="Aumentar zoom"
              >
                <i class="fa-solid fa-magnifying-glass-plus"></i>
              </button>
            </div>
            <div class="columns-dropdown" ref="columnsDropdownHistory">
              <button
                type="button"
                class="btn-action btn-columns"
                @click.stop="showHistoryColumnsMenu = !showHistoryColumnsMenu"
                title="Mostrar ou esconder colunas"
              >
                <i class="fa-solid fa-columns"></i>
                Colunas
                <i class="fa-solid fa-caret-down columns-caret"></i>
              </button>
              <div v-if="showHistoryColumnsMenu" class="columns-menu" @click.stop>
                <div class="columns-menu-header">
                  <span>Colunas visíveis</span>
                  <button type="button" class="columns-reset" @click="resetVisibleHistoryColumns">
                    Restaurar
                  </button>
                </div>
                <label
                  v-for="col in historyToggleableColumns"
                  :key="col.key"
                  class="columns-menu-item"
                  :class="{ locked: col.locked }"
                >
                  <input
                    type="checkbox"
                    :checked="isHistoryColumnVisible(col.key)"
                    :disabled="col.locked"
                    @change="toggleHistoryColumn(col.key)"
                  />
                  <span>{{ col.label }}</span>
                </label>
              </div>
            </div>
            <button
              type="button"
              class="btn-action btn-print"
              @click="printHistoryReport()"
              title="Imprimir relatório"
            >
              <i class="fa-solid fa-print"></i>
              Imprimir
            </button>
            <button
              type="button"
              class="btn-action btn-pdf"
              @click="exportHistoryToPDF()"
              title="Exportar para PDF"
            >
              <i class="fa-solid fa-file-pdf"></i>
              PDF
            </button>
            <button
              type="button"
              class="btn-action btn-excel"
              @click="exportHistoryToExcel()"
              title="Exportar para Excel"
            >
              <i class="fa-solid fa-file-excel"></i>
              Excel
            </button>
          </div>
        </div>
        
        <div class="table-container" :style="tableZoomStyle">
          <table class="modern-table">
            <thead>
              <tr>
                <th v-if="isHistoryColumnVisible('seller')">Vendedor</th>
                <th v-if="isHistoryColumnVisible('collector')">Cobrador</th>
                <th v-if="isHistoryColumnVisible('previousBalance')">Saldo Ant.</th>
                <th v-if="isHistoryColumnVisible('sales')">Vendas(+)</th>
                <th v-if="isHistoryColumnVisible('commission')">Comissão(-)</th>
                <th v-if="isHistoryColumnVisible('liquid')">Líquido</th>
                <th v-if="shouldShowRetainedPrizesColumn && isHistoryColumnVisible('retainedPrizes')">Prêmios Retidos</th>
                <th v-if="isHistoryColumnVisible('prizes')">Prêmios(-)</th>
                <th v-if="isHistoryColumnVisible('liquidoTwo')">{{ liquidoTwoColumnTitle }}</th>
                <th v-if="shouldShowRetorno && isHistoryColumnVisible('returnValue')">Retorno</th>
                <th
                  v-if="isHistoryColumnVisible('banca')"
                  colspan="2"
                  class="banca-header"
                >
                  <div class="banca-header-content">
                    <span>Banca Recebeu(+)</span>
                    <span>Banca Pagou(-)</span>
                  </div>
                </th>
                <th v-if="isHistoryColumnVisible('currentBalance')">Saldo Atual</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="historyData.length === 0" class="empty-row">
                <td :colspan="historyEmptyColspan" class="empty-message">
                  <div class="empty-table-message">
                    <i class="fa-solid fa-info-circle"></i>
                    <span>Nenhum vendedor encontrado para os filtros selecionados</span>
                  </div>
                </td>
              </tr>
              <tr 
                v-for="seller in historyData" 
                :key="seller.id"
                class="data-row"
              >
                <td v-if="isHistoryColumnVisible('seller')">
                  <div class="seller-info">
                    <span class="seller-name">{{ seller.name }}</span>
                    <span v-if="!seller.isActive" class="status-badge badge-inactive">Inativo</span>
                  </div>
                </td>
                <td v-if="isHistoryColumnVisible('collector')">
                  <span class="collector-name">{{ seller.collectorName }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('previousBalance')">
                  <span :class="['currency-value', { 'currency-positive': seller.previousBalance > 0, 'currency-negative': seller.previousBalance < 0 }]">
                    {{ setCurrencyString(seller.previousBalance) }}
                  </span>
                </td>
                <td v-if="isHistoryColumnVisible('sales')">
                  <span
                    v-if="(seller.sales || 0) > 0"
                    class="currency-value currency-positive clickable-value"
                    @click="openSalesModal(seller)"
                    title="Clique para ver as vendas"
                  >
                    {{ setCurrencyString(seller.sales) }}
                  </span>
                  <span v-else class="currency-value currency-positive">{{ setCurrencyString(seller.sales) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('commission')">
                  <span class="currency-value currency-negative">{{ setCurrencyString(seller.commission) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('liquid')">
                  <span :class="['currency-value', { 'currency-positive': seller.liquid > 0, 'currency-negative': seller.liquid < 0 }]">
                    {{ setCurrencyString(seller.liquid) }}
                  </span>
                </td>
                <td v-if="shouldShowRetainedPrizesColumn && isHistoryColumnVisible('retainedPrizes')">
                  <span class="currency-value currency-negative">{{ setCurrencyString(seller.retainedPrizes || 0) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('prizes')">
                  <span class="currency-value currency-negative">{{ setCurrencyString(seller.prizes) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('liquidoTwo')">
                  <span :class="['currency-value', { 'currency-positive': calculateLiquidoTwo(seller) > 0, 'currency-negative': calculateLiquidoTwo(seller) < 0 }]">
                    {{ setCurrencyString(calculateLiquidoTwo(seller)) }}
                  </span>
                </td>
                <td v-if="shouldShowRetorno && isHistoryColumnVisible('returnValue')">
                  <span class="currency-value">{{ setCurrencyString(calculateReturnValue(seller)) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('banca')" colspan="2" class="banca-cell">
                  <div class="banca-container">
                    <div class="banca-values">
                      <div class="banca-recebeu">
                        <span class="currency-value currency-positive">{{ setCurrencyString(seller.credit) }}</span>
                      </div>
                      <div class="banca-pagou">
                        <span class="currency-value currency-negative">{{ setCurrencyString(seller.debit) }}</span>
                      </div>
                    </div>
                    <div class="banca-resultado">
                      <i class="fa-solid fa-caret-down resultado-icon"></i>
                      <span :class="['currency-value', 'resultado-value', { 'currency-negative': (seller.credit - seller.debit) < 0, 'currency-positive': (seller.credit - seller.debit) > 0 }]">
                        {{ setCurrencyString(seller.credit - seller.debit) }}
                      </span>
                    </div>
                  </div>
                </td>
                <td v-if="isHistoryColumnVisible('currentBalance')">
                  <span :class="['currency-value', { 'currency-negative': seller.currentBalance < 0, 'currency-positive': seller.currentBalance > 0 }]">
                    {{ setCurrencyString(seller.currentBalance) }}
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td :colspan="historyTotalLabelColspan">
                  <div class="total-cell">
                    <i class="fa-solid fa-calculator"></i>
                    <span>Total</span>
                  </div>
                </td>
                <td v-if="isHistoryColumnVisible('previousBalance')">
                  <span :class="['currency-value', { 'currency-positive': totals.previousBalance > 0, 'currency-negative': totals.previousBalance < 0 }]">
                    {{ setCurrencyString(totals.previousBalance) }}
                  </span>
                </td>
                <td v-if="isHistoryColumnVisible('sales')">
                  <span class="currency-value currency-positive">{{ setCurrencyString(totals.sales) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('commission')">
                  <span class="currency-value currency-negative">{{ setCurrencyString(totals.commission) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('liquid')">
                  <span :class="['currency-value', { 'currency-positive': totals.liquid > 0, 'currency-negative': totals.liquid < 0 }]">
                    {{ setCurrencyString(totals.liquid) }}
                  </span>
                </td>
                <td v-if="shouldShowRetainedPrizesColumn && isHistoryColumnVisible('retainedPrizes')">
                  <span class="currency-value currency-negative">{{ setCurrencyString(totals.retainedPrizes || 0) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('prizes')">
                  <span class="currency-value currency-negative">{{ setCurrencyString(totals.prizes) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('liquidoTwo')">
                  <span :class="['currency-value', { 'currency-positive': calculateTotalLiquidoTwo() > 0, 'currency-negative': calculateTotalLiquidoTwo() < 0 }]">
                    {{ setCurrencyString(calculateTotalLiquidoTwo()) }}
                  </span>
                </td>
                <td v-if="shouldShowRetorno && isHistoryColumnVisible('returnValue')">
                  <span class="currency-value">{{ setCurrencyString(calculateTotalReturnValue()) }}</span>
                </td>
                <td v-if="isHistoryColumnVisible('banca')" colspan="2" class="banca-cell">
                  <div class="banca-container">
                    <div class="banca-values">
                      <div class="banca-recebeu">
                        <span class="currency-value currency-positive">{{ setCurrencyString(totals.credit) }}</span>
                      </div>
                      <div class="banca-pagou">
                        <span class="currency-value currency-negative">{{ setCurrencyString(totals.debit) }}</span>
                      </div>
                    </div>
                    <div class="banca-resultado">
                      <i class="fa-solid fa-caret-down resultado-icon"></i>
                      <span :class="['currency-value', 'resultado-value', { 'currency-negative': (totals.credit - totals.debit) < 0, 'currency-positive': (totals.credit - totals.debit) > 0 }]">
                        {{ setCurrencyString(totals.credit - totals.debit) }}
                      </span>
                    </div>
                  </div>
                </td>
                <td v-if="isHistoryColumnVisible('currentBalance')">
                  <span :class="['currency-value', 'final-balance', { 'currency-negative': totals.currentBalance < 0, 'currency-positive': totals.currentBalance > 0 }]">
                    {{ setCurrencyString(totals.currentBalance) }}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Tabela de Registros de Pagamentos -->
      <div v-show="hasSearched && selectedPayments.length > 0" class="results-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-receipt"></i>
            Registros de Pagamentos
          </h3>
          <div class="card-actions">
            <div class="table-zoom-controls" title="Zoom da tabela">
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom <= 5"
                @click="decreaseTableZoom"
                title="Diminuir zoom"
              >
                <i class="fa-solid fa-magnifying-glass-minus"></i>
              </button>
              <span class="zoom-label" @click="resetTableZoom" title="Restaurar 100%">
                {{ tableZoom }}%
              </span>
              <button
                type="button"
                class="btn-zoom"
                :disabled="tableZoom >= 100"
                @click="increaseTableZoom"
                title="Aumentar zoom"
              >
                <i class="fa-solid fa-magnifying-glass-plus"></i>
              </button>
            </div>
            <div class="columns-dropdown" ref="columnsDropdownPayments">
              <button
                type="button"
                class="btn-action btn-columns"
                @click.stop="showPaymentsColumnsMenu = !showPaymentsColumnsMenu"
                title="Mostrar ou esconder colunas"
              >
                <i class="fa-solid fa-columns"></i>
                Colunas
                <i class="fa-solid fa-caret-down columns-caret"></i>
              </button>
              <div v-if="showPaymentsColumnsMenu" class="columns-menu" @click.stop>
                <div class="columns-menu-header">
                  <span>Colunas visíveis</span>
                  <button type="button" class="columns-reset" @click="resetVisiblePaymentsColumns">
                    Restaurar
                  </button>
                </div>
                <label
                  v-for="col in paymentsToggleableColumns"
                  :key="col.key"
                  class="columns-menu-item"
                  :class="{ locked: col.locked }"
                >
                  <input
                    type="checkbox"
                    :checked="isPaymentsColumnVisible(col.key)"
                    :disabled="col.locked"
                    @change="togglePaymentsColumn(col.key)"
                  />
                  <span>{{ col.label }}</span>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div class="table-container" :style="tableZoomStyle">
          <table class="modern-table">
            <thead>
              <tr>
                <th v-if="isPaymentsColumnVisible('paymentDate')">Data Pagamento</th>
                <th v-if="isPaymentsColumnVisible('collector')">Cobrador</th>
                <th v-if="isPaymentsColumnVisible('period')">Período</th>
                <th v-if="isPaymentsColumnVisible('commission')">Comissão</th>
                <th v-if="isPaymentsColumnVisible('bonus')">Bônus</th>
                <th v-if="isPaymentsColumnVisible('total')">Total</th>
                <th v-if="isPaymentsColumnVisible('paidBy')">Pago Por</th>
                <th v-if="isPaymentsColumnVisible('observations')">Observações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="payment in selectedPayments" 
                :key="payment.id"
                class="data-row"
              >
                <td v-if="isPaymentsColumnVisible('paymentDate')">
                  <span class="payment-date">{{ formatDate(payment.paymentDate) }}</span>
                </td>
                <td v-if="isPaymentsColumnVisible('collector')">
                  <span class="collector-name">{{ payment.collectorName }}</span>
                </td>
                <td v-if="isPaymentsColumnVisible('period')">
                  <span class="payment-period">
                    {{ formatDateShort(payment.paymentPeriodStart) }} - {{ formatDateShort(payment.paymentPeriodEnd) }}
                  </span>
                </td>
                <td v-if="isPaymentsColumnVisible('commission')">
                  <span class="currency-value currency-positive">{{ setCurrencyString(payment.commissionAmount) }}</span>
                </td>
                <td v-if="isPaymentsColumnVisible('bonus')">
                  <span class="currency-value currency-positive">{{ setCurrencyString(payment.bonusAmount) }}</span>
                </td>
                <td v-if="isPaymentsColumnVisible('total')">
                  <span class="currency-value currency-positive total-amount">{{ setCurrencyString(payment.totalAmount) }}</span>
                </td>
                <td v-if="isPaymentsColumnVisible('paidBy')">
                  <span class="paid-by">{{ payment.paidByUserName }}</span>
                </td>
                <td v-if="isPaymentsColumnVisible('observations')">
                  <span class="payment-notes">{{ payment.notes || '-' }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-show="!hasSearched" class="empty-state">
        <div class="empty-state-content">
          <i class="fa-solid fa-chart-line"></i>
          <h3>Realize uma consulta</h3>
          <p>Selecione os cobradores e o período para consultar o histórico</p>
        </div>
      </div>
    </div>

    <!-- Modal de Vendas -->
    <PrizeTicketsModal
      v-if="showSalesModal"
      :sellerId="salesModalSellerId"
      :regionId="salesModalRegionId"
      :regionName="salesModalEntityName"
      :dateBegin="startDate"
      :dateEnd="endDate"
      mode="sales"
      @close="closeSalesModal"
    />
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";
import jsFunctions from "@/services/jsFunctions";
import MultiSelect from "@/components/utils/MultiSelect.vue";
import PrizeTicketsModal from "@/components/PrizeTicketsModal.vue";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const COLLECTOR_HISTORY_COLUMNS_STORAGE_KEY = "collectorHistory.visibleColumns";
const COLLECTOR_PAYMENTS_COLUMNS_STORAGE_KEY = "collectorHistory.payments.visibleColumns";
const TABLE_ZOOM_STORAGE_KEY = "ab.resultsTableZoom";
const TABLE_ZOOM_MIN = 5;
const TABLE_ZOOM_MAX = 100;
const TABLE_ZOOM_STEP = 5;

const DEFAULT_VISIBLE_HISTORY_COLUMNS = {
  seller: true,
  collector: true,
  previousBalance: true,
  sales: true,
  commission: true,
  liquid: true,
  retainedPrizes: true,
  prizes: true,
  liquidoTwo: true,
  returnValue: true,
  banca: true,
  currentBalance: true,
};

const DEFAULT_VISIBLE_PAYMENTS_COLUMNS = {
  paymentDate: true,
  collector: true,
  period: true,
  commission: true,
  bonus: true,
  total: true,
  paidBy: true,
  observations: true,
};

export default {
  name: "ViewCollectorHistory",
  components: { MultiSelect, PrizeTicketsModal },
  data: () => ({
    // Filtros
    selectedCollectors: [],
    selectedCollectorsObjects: [],
    includeInactiveSellers: "true",
    commissionType: "BRUTO/LIQUIDO",
    liquidPrize: "SEM DESPESA",
    startDate: "",
    endDate: "",
    
    // Dados
    availableCollectors: [],
    historyData: [],
    totals: {
      previousBalance: 0,
      sales: 0,
      commission: 0,
      autoCommission: 0,
      liquid: 0,
      prizes: 0,
      liquidPrize: 0,
      retainedPrizes: 0,
      credit: 0,
      debit: 0,
      expenses: 0,
      totalValue: 0,
      currentBalance: 0
    },
    statistics: {
      active: 0,
      withSales: 0,
      withoutSales: 0
    },
    
    // Modal de vendas
    showSalesModal: false,
    salesModalSellerId: null,
    salesModalRegionId: null,
    salesModalEntityName: '',

    // Pagamentos
    selectedPayments: [],
    
    // Estado
    loading: true,
    hasSearched: false,
    currentUser: null,
    userResponsibleRegions: [],
    bancas: [],
    regions: [],

    // Controle de colunas
    showHistoryColumnsMenu: false,
    showPaymentsColumnsMenu: false,
    visibleHistoryColumns: { ...DEFAULT_VISIBLE_HISTORY_COLUMNS },
    visiblePaymentsColumns: { ...DEFAULT_VISIBLE_PAYMENTS_COLUMNS },
    tableZoom: 100,
  }),
  computed: {
    tableZoomStyle() {
      return { zoom: this.tableZoom / 100 };
    },

    currentSelectedRegion() {
      const savedRegion = localStorage.getItem('currentRegion');
      return savedRegion ? JSON.parse(savedRegion) : null;
    },

    currentSelectedBanca() {
      const savedBanca = localStorage.getItem('selectedBanca');
      return savedBanca ? JSON.parse(savedBanca) : null;
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    shouldShowRetainedPrizesColumn() {
      // Mostrar coluna "Prêmio Retido" apenas para regiões não de descarga (onlyDescarga = false)
      // Se TODAS as regiões têm onlyDescarga = true, então NÃO mostrar "Prêmio Retido"
      if (!this.historyData || this.historyData.length === 0) {
        return true; // Mostrar por padrão se não há dados
      }
      
      // Verificar se TODAS as regiões dos vendedores são de descarga
      const allSellersAreFromDescargaRegions = this.historyData.every(seller => {
        const region = this.regions.find(r => r.id === seller.regionId);
        return region && region.onlyDescarga === true;
      });
      
      // Se TODAS são de descarga, NÃO mostrar a coluna
      // Se pelo menos uma NÃO é de descarga, mostrar a coluna
      return !allSellersAreFromDescargaRegions;
    },

    shouldShowRetorno() {
      // Mostrar Retorno apenas se há pelo menos uma região de descarga
      if (!this.historyData || this.historyData.length === 0) {
        return false;
      }
      
      // Verificar se há pelo menos um vendedor de região de descarga
      return this.historyData.some(seller => {
        const region = this.regions.find(r => r.id === seller.regionId);
        return region && region.onlyDescarga;
      });
    },

    liquidoTwoColumnTitle() {
      // Se todas as regiões NÃO são de descarga, mostrar "LP", senão "Líquido 2"
      if (!this.historyData || this.historyData.length === 0) {
        return 'LP'; // Padrão
      }
      
      // Verificar se TODAS as regiões dos vendedores NÃO são de descarga
      const allSellersAreNotFromDescargaRegions = this.historyData.every(seller => {
        const region = this.regions.find(r => r.id === seller.regionId);
        return region && !region.onlyDescarga;
      });
      
      return allSellersAreNotFromDescargaRegions ? 'LP' : 'Líquido 2';
    },

    historyToggleableColumns() {
      const columns = [
        { key: "seller", label: "Vendedor", locked: true },
        { key: "collector", label: "Cobrador" },
        { key: "previousBalance", label: "Saldo Ant." },
        { key: "sales", label: "Vendas(+)" },
        { key: "commission", label: "Comissão(-)" },
        { key: "liquid", label: "Líquido" },
      ];

      if (this.shouldShowRetainedPrizesColumn) {
        columns.push({ key: "retainedPrizes", label: "Prêmios Retidos" });
      }

      columns.push(
        { key: "prizes", label: "Prêmios(-)" },
        { key: "liquidoTwo", label: this.liquidoTwoColumnTitle },
      );

      if (this.shouldShowRetorno) {
        columns.push({ key: "returnValue", label: "Retorno" });
      }

      columns.push(
        { key: "banca", label: "Banca Recebeu/Pagou" },
        { key: "currentBalance", label: "Saldo Atual" },
      );

      return columns;
    },

    paymentsToggleableColumns() {
      return [
        { key: "paymentDate", label: "Data Pagamento", locked: true },
        { key: "collector", label: "Cobrador" },
        { key: "period", label: "Período" },
        { key: "commission", label: "Comissão" },
        { key: "bonus", label: "Bônus" },
        { key: "total", label: "Total" },
        { key: "paidBy", label: "Pago Por" },
        { key: "observations", label: "Observações" },
      ];
    },

    historyEmptyColspan() {
      return this.historyToggleableColumns.reduce((count, col) => {
        if (!this.isHistoryColumnVisible(col.key)) {
          return count;
        }
        return count + (col.key === "banca" ? 2 : 1);
      }, 0) || 1;
    },

    historyTotalLabelColspan() {
      let colspan = 0;
      if (this.isHistoryColumnVisible("seller")) {
        colspan += 1;
      }
      if (this.isHistoryColumnVisible("collector")) {
        colspan += 1;
      }
      return colspan || 1;
    },
  },
  async mounted() {
    this.loadVisibleHistoryColumns();
    this.loadVisiblePaymentsColumns();
    this.loadTableZoom();
    document.addEventListener("click", this.handleColumnsMenuOutsideClick);
    this.loading = true;
    try {
      await this.loadCurrentUser();
      
      // Carregar bancas primeiro
      await this.loadBancas();
      
      // Carregar regiões
      await this.loadRegions();
      
      // Para usuários BANCA, complementar regiões responsáveis com regiões das bancas
      if (this.isBanca) {
        await this.loadBancaRegions();
      }
      
      await this.loadCollectors();
      this.setDefaultDates();
    } catch (error) {
      toast.error(handleErrorMessage(error));
    } finally {
      this.loading = false;
    }
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleColumnsMenuOutsideClick);
  },
  watch: {
    // Sincronizar selectedCollectorsObjects com selectedCollectors
    selectedCollectorsObjects: {
      handler(newObjects) {
        this.selectedCollectors = newObjects.map(obj => obj.id);
      },
      deep: true
    },
    currentSelectedRegion: {
      handler(newRegion, oldRegion) {
        // Se a região mudou, limpar seleções e recarregar cobradores
        this.selectedCollectors = [];
        this.selectedCollectorsObjects = [];
        this.historyData = [];
        this.hasSearched = false;
        
        // Recarregar cobradores com o novo filtro
        this.loadCollectors();
      },
      deep: true
    },

    currentSelectedBanca: {
      handler(newBanca, oldBanca) {
        // Se a banca mudou, limpar seleções e recarregar cobradores
        this.selectedCollectors = [];
        this.selectedCollectorsObjects = [];
        this.historyData = [];
        this.hasSearched = false;
        
        // Recarregar cobradores com o novo filtro
        this.loadCollectors();
      },
      deep: true
    }
  },
  methods: {
    loadVisibleHistoryColumns() {
      try {
        const saved = localStorage.getItem(COLLECTOR_HISTORY_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.visibleHistoryColumns = { ...DEFAULT_VISIBLE_HISTORY_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.visibleHistoryColumns = {
          ...DEFAULT_VISIBLE_HISTORY_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          seller: true,
        };
      } catch {
        this.visibleHistoryColumns = { ...DEFAULT_VISIBLE_HISTORY_COLUMNS };
      }
    },

    loadVisiblePaymentsColumns() {
      try {
        const saved = localStorage.getItem(COLLECTOR_PAYMENTS_COLUMNS_STORAGE_KEY);
        if (!saved) {
          this.visiblePaymentsColumns = { ...DEFAULT_VISIBLE_PAYMENTS_COLUMNS };
          return;
        }
        const parsed = JSON.parse(saved);
        this.visiblePaymentsColumns = {
          ...DEFAULT_VISIBLE_PAYMENTS_COLUMNS,
          ...(parsed && typeof parsed === "object" ? parsed : {}),
          paymentDate: true,
        };
      } catch {
        this.visiblePaymentsColumns = { ...DEFAULT_VISIBLE_PAYMENTS_COLUMNS };
      }
    },

    loadTableZoom() {
      try {
        const saved = parseInt(localStorage.getItem(TABLE_ZOOM_STORAGE_KEY), 10);
        if (!Number.isNaN(saved) && saved >= TABLE_ZOOM_MIN && saved <= TABLE_ZOOM_MAX) {
          this.tableZoom = saved;
          return;
        }
      } catch {
        // ignore
      }
      this.tableZoom = 100;
    },

    saveTableZoom() {
      localStorage.setItem(TABLE_ZOOM_STORAGE_KEY, String(this.tableZoom));
    },

    decreaseTableZoom() {
      if (this.tableZoom <= TABLE_ZOOM_MIN) return;
      this.tableZoom = Math.max(TABLE_ZOOM_MIN, this.tableZoom - TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    increaseTableZoom() {
      if (this.tableZoom >= TABLE_ZOOM_MAX) return;
      this.tableZoom = Math.min(TABLE_ZOOM_MAX, this.tableZoom + TABLE_ZOOM_STEP);
      this.saveTableZoom();
    },

    resetTableZoom() {
      this.tableZoom = 100;
      this.saveTableZoom();
    },

    saveVisibleHistoryColumns() {
      localStorage.setItem(
        COLLECTOR_HISTORY_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.visibleHistoryColumns),
      );
    },

    saveVisiblePaymentsColumns() {
      localStorage.setItem(
        COLLECTOR_PAYMENTS_COLUMNS_STORAGE_KEY,
        JSON.stringify(this.visiblePaymentsColumns),
      );
    },

    isHistoryColumnVisible(key) {
      if (key === "seller") {
        return true;
      }
      return this.visibleHistoryColumns[key] !== false;
    },

    isPaymentsColumnVisible(key) {
      if (key === "paymentDate") {
        return true;
      }
      return this.visiblePaymentsColumns[key] !== false;
    },

    toggleHistoryColumn(key) {
      if (key === "seller") {
        return;
      }
      this.visibleHistoryColumns = {
        ...this.visibleHistoryColumns,
        [key]: !this.isHistoryColumnVisible(key),
      };
      this.saveVisibleHistoryColumns();
    },

    togglePaymentsColumn(key) {
      if (key === "paymentDate") {
        return;
      }
      this.visiblePaymentsColumns = {
        ...this.visiblePaymentsColumns,
        [key]: !this.isPaymentsColumnVisible(key),
      };
      this.saveVisiblePaymentsColumns();
    },

    resetVisibleHistoryColumns() {
      this.visibleHistoryColumns = { ...DEFAULT_VISIBLE_HISTORY_COLUMNS };
      this.saveVisibleHistoryColumns();
    },

    resetVisiblePaymentsColumns() {
      this.visiblePaymentsColumns = { ...DEFAULT_VISIBLE_PAYMENTS_COLUMNS };
      this.saveVisiblePaymentsColumns();
    },

    handleColumnsMenuOutsideClick(event) {
      const historyDropdown = this.$refs.columnsDropdownHistory;
      if (
        this.showHistoryColumnsMenu &&
        historyDropdown &&
        !historyDropdown.contains(event.target)
      ) {
        this.showHistoryColumnsMenu = false;
      }

      const paymentsDropdown = this.$refs.columnsDropdownPayments;
      if (
        this.showPaymentsColumnsMenu &&
        paymentsDropdown &&
        !paymentsDropdown.contains(event.target)
      ) {
        this.showPaymentsColumnsMenu = false;
      }
    },

    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
          
          // Se não for ADMIN, carregar regiões do usuário responsável  
          if (this.currentUser.role !== 0) {
            await this.loadUserResponsibleRegions();
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error);
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        console.error('Erro ao carregar regiões do usuário:', error);
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async loadBancaRegions() {
      try {
        if (!this.currentUser || !this.isBanca) return;
        
        // Buscar bancas disponíveis para o usuário BANCA
        const bancasForUser = this.bancas.filter(banca => 
          banca.responsibleUserIds && banca.responsibleUserIds.includes(this.currentUser.id)
        );
        
        for (const banca of bancasForUser) {
          if (banca.regionIds && Array.isArray(banca.regionIds)) {
            // Adicionar todas as regiões desta banca às regiões responsáveis
            banca.regionIds.forEach(regionId => {
              if (!this.userResponsibleRegions.includes(regionId)) {
                this.userResponsibleRegions.push(regionId);
              }
            });
          }
        }
      } catch (error) {
        console.error('Erro ao carregar regiões das bancas:', error);
      }
    },

    async loadBancas() {
      try {
        const response = await mainService.searchBancas({ isActive: true });
        this.bancas = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar bancas:', error);
      }
    },

    async loadRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar regiões:', error);
      }
    },

    async loadCollectors() {
      try {
        // PRIMEIRO: Carregar bancas e regiões se ainda não foram carregadas
        if (!this.bancas || this.bancas.length === 0) {
          await this.loadBancas();
        }
        
        if (!this.regions || this.regions.length === 0) {
          await this.loadRegions();
        }

        // SEGUNDO: Buscar cobradores
        const response = await mainService.searchCollectors();
        
        // TERCEIRO: Mapear dados dos cobradores com bancaId ANTES de filtrar
        let collectors = response.data.map(collector => {
          // Buscar a região do cobrador para obter a banca
          const collectorRegion = this.regions.find(r => r.id === collector.regionId);
          const collectorBanca = collectorRegion ? this.bancas.find(b => b.id === collectorRegion.bancaId) : null;
          
          return {
            id: collector.id,
            name: `${collector.username} - ${collector.name}`, // Nome para exibição no MultiSelect
            username: collector.username,
            fullName: collector.name,
            regionId: collector.regionId,
            regionName: collector.regionName,
            bancaId: collectorBanca?.id || null, // ID da banca baseado na região
            bancaName: collectorBanca?.name || '-', // Nome da banca baseado na região
            active: collector.isActive
          };
        });

        // QUARTO: Aplicar filtros baseado no role do usuário
        if (this.isCobrador) {
          // COBRADOR só vê a si mesmo
          collectors = collectors.filter(collector => 
            String(collector.id) === String(this.currentUser.id)
          );
        } else if (!this.isAdmin) {
          // BANCA e GERENTE veem apenas cobradores de suas regiões
          collectors = collectors.filter(collector => {
            return this.userResponsibleRegions.some(
              id => String(id) === String(collector.regionId)
            );
          });
          
          // Aplicar filtro por região ativa selecionada se houver
          if (this.currentSelectedRegion) {
            collectors = collectors.filter(collector => {
              return String(collector.regionId) === String(this.currentSelectedRegion.id);
            });
          }
        } else if (this.isAdmin) {
          // ADMIN: aplicar filtro por banca selecionada primeiro
          if (this.currentSelectedBanca) {
            // Filtrar cobradores pela banca selecionada
            collectors = collectors.filter(collector => {
              return String(collector.bancaId) === String(this.currentSelectedBanca.id);
            });
          }
          
          // Em seguida, aplicar filtro por região apenas se houver região selecionada
          if (this.currentSelectedRegion) {
            collectors = collectors.filter(collector => {
              return String(collector.regionId) === String(this.currentSelectedRegion.id);
            });
          }
          // Se currentSelectedRegion é null, não aplica filtro de região (mostra todos)
        }

        this.availableCollectors = [...collectors].sort((a, b) =>
          (a.fullName || a.name || '').localeCompare(b.fullName || b.name || '', 'pt-BR', { sensitivity: 'base' })
        );

        // Se for cobrador, selecionar automaticamente
        if (this.isCobrador && this.availableCollectors.length > 0) {
          this.selectedCollectors = [this.availableCollectors[0].id];
          this.selectedCollectorsObjects = [this.availableCollectors[0]];
        }

      } catch (error) {
        console.error('Erro ao carregar cobradores:', error);
        toast.error(handleErrorMessage(error));
      }
    },

    async loadSelectedCollectorPayments() {
      const collectorIds = this.isCobrador
        ? [this.currentUser.id]
        : this.selectedCollectors;

      if (!collectorIds.length) {
        this.selectedPayments = [];
        return;
      }

      try {
        // Carregar pagamentos para cobradores selecionados
        const paymentPromises = collectorIds.map(async (collectorId) => {
          try {
            const response = await mainService.getCollectorPayments(collectorId);
            return response.data || [];
          } catch (error) {
            console.error(`Erro ao carregar pagamentos do cobrador ${collectorId}:`, error);
            return [];
          }
        });

        const paymentsArrays = await Promise.all(paymentPromises);
        
        // Combinar e ordenar todos os pagamentos
        this.selectedPayments = paymentsArrays
          .flat()
          .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
      } catch (error) {
        console.error('Erro ao carregar pagamentos:', error);
      }
    },

    setDefaultDates() {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const _fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      this.startDate = _fmt(startOfMonth);
      this.endDate = _fmt(today);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },

    formatDateShort(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    },

    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },

    openSalesModal(seller) {
      if (!seller || (seller.sales || 0) <= 0) return;
      this.salesModalSellerId = seller.id || seller.sellerId || null;
      this.salesModalRegionId = seller.regionId || null;
      this.salesModalEntityName = seller.name || '';
      this.showSalesModal = true;
    },

    closeSalesModal() {
      this.showSalesModal = false;
      this.salesModalSellerId = null;
      this.salesModalRegionId = null;
      this.salesModalEntityName = '';
    },

    filterSellersByUserAccess(sellers) {
      if (!sellers || !sellers.length) return [];

      let filtered = [...sellers];

      // Cobrador: apenas vendedores vinculados a ele
      if (this.isCobrador) {
        const collectorId = this.currentUser?.id;
        filtered = filtered.filter((seller) => {
          const sellerCollectorId = seller.collectorId || seller.collectorUserId;
          if (sellerCollectorId != null) {
            return String(sellerCollectorId) === String(collectorId);
          }
          // Fallback: se a API não enviar collectorId, restringe aos cobradores permitidos
          return this.selectedCollectors.some(
            (id) => String(id) === String(collectorId)
          );
        });
      }

      // Não-admin: apenas vendedores das regiões do usuário
      if (!this.isAdmin) {
        filtered = filtered.filter((seller) =>
          this.userResponsibleRegions.some(
            (id) => String(id) === String(seller.regionId)
          )
        );

        if (this.currentSelectedRegion) {
          filtered = filtered.filter(
            (seller) =>
              String(seller.regionId) === String(this.currentSelectedRegion.id)
          );
        }
      } else if (this.currentSelectedRegion) {
        filtered = filtered.filter(
          (seller) =>
            String(seller.regionId) === String(this.currentSelectedRegion.id)
        );
      }

      return filtered;
    },

    recalculateTotalsFromSellers(sellers) {
      const empty = {
        previousBalance: 0,
        sales: 0,
        commission: 0,
        autoCommission: 0,
        liquid: 0,
        prizes: 0,
        liquidPrize: 0,
        retainedPrizes: 0,
        credit: 0,
        debit: 0,
        expenses: 0,
        totalValue: 0,
        currentBalance: 0,
      };

      return (sellers || []).reduce((acc, seller) => {
        acc.previousBalance += Number(seller.previousBalance) || 0;
        acc.sales += Number(seller.sales) || 0;
        acc.commission += Number(seller.commission) || 0;
        acc.autoCommission += Number(seller.autoCommission) || 0;
        acc.liquid += Number(seller.liquid) || 0;
        acc.prizes += Number(seller.prizes) || 0;
        acc.liquidPrize += Number(seller.liquidPrize) || 0;
        acc.retainedPrizes += Number(seller.retainedPrizes) || 0;
        acc.credit += Number(seller.credit) || 0;
        acc.debit += Number(seller.debit) || 0;
        acc.expenses += Number(seller.expenses) || 0;
        acc.totalValue += Number(seller.totalValue) || 0;
        acc.currentBalance += Number(seller.currentBalance) || 0;
        return acc;
      }, empty);
    },

    async searchHistory() {
      if (this.selectedCollectors.length === 0) {
        toast.error("Selecione pelo menos um cobrador!");
        return;
      }

      if (!this.startDate || !this.endDate) {
        toast.error("Selecione o período de consulta!");
        return;
      }

      if (new Date(this.startDate) > new Date(this.endDate)) {
        toast.error("A data inicial deve ser anterior à data final!");
        return;
      }

      this.loading = true;
      this.hasSearched = false;

      try {
        const dateBeginStr = this.startDate + "T00:00:00";
        const dateEndStr = this.endDate + "T23:59:59";

        // Cobrador só consulta a si mesmo, independentemente da seleção
        const collectorIds = this.isCobrador
          ? [this.currentUser.id]
          : this.selectedCollectors;

        const request = {
          collectorIds,
          startDate: dateBeginStr,
          endDate: dateEndStr,
          includeInactiveSellers: this.includeInactiveSellers === 'true',
          commissionType: this.commissionType,
          liquidPrize: this.liquidPrize
        };

        const response = await mainService.searchCollectorHistory(request);
        
        // Filtrar vendedores por região (e vínculo do cobrador)
        this.historyData = this.filterSellersByUserAccess(response.data.sellers || [])
          .sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
          );

        this.totals = this.recalculateTotalsFromSellers(this.historyData);
        this.statistics = {
          active: this.historyData.filter((s) => s.isActive !== false).length,
          withSales: this.historyData.filter((s) => (Number(s.sales) || 0) > 0).length,
          withoutSales: this.historyData.filter((s) => (Number(s.sales) || 0) <= 0).length,
        };
        
        this.hasSearched = true;
        
        // Carregar pagamentos dos cobradores selecionados
        await this.loadSelectedCollectorPayments();
        
        toast.success("Histórico carregado com sucesso!");
      } catch (error) {
        console.error('Erro ao buscar histórico:', error);
        toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    calculateLiquidoTwo(seller) {
      // Líquido 2 = (Líquido - Prêmios Pagos)
      // Nota: Agora só consideramos prêmios efetivamente pagos, não os retidos
      const liquid = seller.liquid || 0;
      const prizes = seller.prizes || 0;
      return liquid - prizes;
    },

    calculateReturnValue(seller) {
      // Calcular o Líquido 2 primeiro
      const liquidoTwo = this.calculateLiquidoTwo(seller);
      
      // Só calcular retorno se o Líquido 2 for positivo
      if (liquidoTwo > 0) {
        // Buscar o percentual de retorno da região do vendedor
        const region = this.regions.find(r => r.id === seller.regionId);
        const returnPercent = region?.descargaReturnPercent || 0;
        return (liquidoTwo * returnPercent) / 100;
      }
      return 0;
    },

    calculateTotalReturnValue() {
      let totalReturnValue = 0;
      if (this.historyData && this.historyData.length > 0) {
        this.historyData.forEach(seller => {
          totalReturnValue += this.calculateReturnValue(seller);
        });
      }
      return totalReturnValue;
    },

    calculateTotalLiquidoTwo() {
      let totalLiquidoTwo = 0;
      if (this.historyData && this.historyData.length > 0) {
        this.historyData.forEach(seller => {
          totalLiquidoTwo += this.calculateLiquidoTwo(seller);
        });
      }
      return totalLiquidoTwo;
    },

    selectedCollectorsLabel() {
      return (this.selectedCollectorsObjects || [])
        .map(c => c.name || c.label || c.displayName)
        .filter(Boolean)
        .join(", ");
    },

    getHistoryExportHeaders() {
      const headers = [];
      if (this.isHistoryColumnVisible("seller")) headers.push("Vendedor");
      if (this.isHistoryColumnVisible("collector")) headers.push("Cobrador");
      if (this.isHistoryColumnVisible("previousBalance")) headers.push("Saldo Ant.");
      if (this.isHistoryColumnVisible("sales")) headers.push("Vendas(+)");
      if (this.isHistoryColumnVisible("commission")) headers.push("Comissão(-)");
      if (this.isHistoryColumnVisible("liquid")) headers.push("Líquido");
      if (
        this.shouldShowRetainedPrizesColumn &&
        this.isHistoryColumnVisible("retainedPrizes")
      ) {
        headers.push("Prêmios Retidos");
      }
      if (this.isHistoryColumnVisible("prizes")) headers.push("Prêmios(-)");
      if (this.isHistoryColumnVisible("liquidoTwo")) {
        headers.push(this.liquidoTwoColumnTitle);
      }
      if (this.shouldShowRetorno && this.isHistoryColumnVisible("returnValue")) {
        headers.push("Retorno");
      }
      if (this.isHistoryColumnVisible("banca")) {
        headers.push("Banca Recebeu(+)");
        headers.push("Banca Pagou(-)");
      }
      if (this.isHistoryColumnVisible("currentBalance")) headers.push("Saldo Atual");
      return headers;
    },

    getHistoryExportRow(seller, formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v || 0) : v || 0;
      const row = [];
      if (this.isHistoryColumnVisible("seller")) {
        const inactive = seller.isActive === false ? " (Inativo)" : "";
        row.push(`${seller.name || ""}${inactive}`);
      }
      if (this.isHistoryColumnVisible("collector")) {
        row.push(seller.collectorName || "");
      }
      if (this.isHistoryColumnVisible("previousBalance")) {
        row.push(money(seller.previousBalance));
      }
      if (this.isHistoryColumnVisible("sales")) row.push(money(seller.sales));
      if (this.isHistoryColumnVisible("commission")) {
        row.push(money(seller.commission));
      }
      if (this.isHistoryColumnVisible("liquid")) row.push(money(seller.liquid));
      if (
        this.shouldShowRetainedPrizesColumn &&
        this.isHistoryColumnVisible("retainedPrizes")
      ) {
        row.push(money(seller.retainedPrizes));
      }
      if (this.isHistoryColumnVisible("prizes")) row.push(money(seller.prizes));
      if (this.isHistoryColumnVisible("liquidoTwo")) {
        row.push(money(this.calculateLiquidoTwo(seller)));
      }
      if (this.shouldShowRetorno && this.isHistoryColumnVisible("returnValue")) {
        row.push(money(this.calculateReturnValue(seller)));
      }
      if (this.isHistoryColumnVisible("banca")) {
        row.push(money(seller.credit));
        row.push(money(seller.debit));
      }
      if (this.isHistoryColumnVisible("currentBalance")) {
        row.push(money(seller.currentBalance));
      }
      return row;
    },

    /** Linha de totais (igual ao rodapé da tabela na página). */
    getHistoryTotalExportRow(formatCurrency = true) {
      const money = (v) =>
        formatCurrency ? this.setCurrencyString(v ?? 0) : Number(v ?? 0);
      const t = this.totals || {};
      const row = [];
      let labelPlaced = false;
      if (this.isHistoryColumnVisible("seller")) {
        row.push("Total");
        labelPlaced = true;
      }
      if (this.isHistoryColumnVisible("collector")) {
        row.push(labelPlaced ? "" : "Total");
      }
      if (this.isHistoryColumnVisible("previousBalance")) {
        row.push(money(t.previousBalance));
      }
      if (this.isHistoryColumnVisible("sales")) row.push(money(t.sales));
      if (this.isHistoryColumnVisible("commission")) {
        row.push(money(t.commission));
      }
      if (this.isHistoryColumnVisible("liquid")) row.push(money(t.liquid));
      if (
        this.shouldShowRetainedPrizesColumn &&
        this.isHistoryColumnVisible("retainedPrizes")
      ) {
        row.push(money(t.retainedPrizes));
      }
      if (this.isHistoryColumnVisible("prizes")) row.push(money(t.prizes));
      if (this.isHistoryColumnVisible("liquidoTwo")) {
        row.push(money(this.calculateTotalLiquidoTwo()));
      }
      if (this.shouldShowRetorno && this.isHistoryColumnVisible("returnValue")) {
        row.push(money(this.calculateTotalReturnValue()));
      }
      if (this.isHistoryColumnVisible("banca")) {
        row.push(money(t.credit));
        row.push(money(t.debit));
      }
      if (this.isHistoryColumnVisible("currentBalance")) {
        row.push(money(t.currentBalance));
      }
      return row;
    },

    printHistoryReport() {
      if (!this.historyData || this.historyData.length === 0) {
        toast.warning("Não há dados para imprimir");
        return;
      }

      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Não foi possível abrir a janela de impressão");
        return;
      }

      printWindow.document.write(this.generateHistoryPrintHTML());
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    generateHistoryPrintHTML() {
      const sellers = this.historyData || [];
      const headers = this.getHistoryExportHeaders();
      const headerRow = headers.map(h => `<th>${h}</th>`).join("");

      let tableRows = "";
      sellers.forEach(seller => {
        const cells = this.getHistoryExportRow(seller, true)
          .map((cell, idx) => {
            const label = headers[idx] || "";
            const isText =
              label === "Vendedor" || label === "Cobrador";
            const align = isText ? "left" : "right";
            const bold = label === "Saldo Atual" ? "font-weight: bold;" : "";
            return `<td style="text-align: ${align}; ${bold}">${cell}</td>`;
          })
          .join("");
        tableRows += `<tr>${cells}</tr>`;
      });

      const totalCells = this.getHistoryTotalExportRow(true)
        .map((cell, idx) => {
          const label = headers[idx] || "";
          const isText = label === "Vendedor" || label === "Cobrador" || idx === 0;
          const align = isText ? "left" : "right";
          return `<td style="text-align: ${align}; font-weight: bold;">${cell}</td>`;
        })
        .join("");

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Histórico Individual dos Vendedores</title>
          <style>
            @media print {
              @page { margin: 1cm; }
              body { margin: 0; }
            }
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            h1 {
              text-align: center;
              color: #2c3e50;
              margin-bottom: 10px;
            }
            .report-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              font-size: 12px;
            }
            th {
              background-color: #e5e5e5;
              padding: 10px;
              text-align: center;
              border: 1px solid #ddd;
              font-weight: bold;
            }
            td {
              padding: 8px;
              border: 1px solid #ddd;
              text-align: center;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .total-row td {
              background-color: #e8eef7;
              font-weight: bold;
              border-top: 2px solid #333;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #666;
            }
          </style>
        </head>
        <body>
          <h1>Relatório de Histórico Individual dos Vendedores</h1>
          <div class="report-info">
            <p><strong>Período:</strong> ${this.startDate} até ${this.endDate}</p>
            <p><strong>Cobradores:</strong> ${this.selectedCollectorsLabel()}</p>
            <p><strong>Data de geração:</strong> ${jsFunctions.setDateTimeDisplay(new Date().toISOString())}</p>
          </div>
          <table>
            <thead>
              <tr>${headerRow}</tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
            <tfoot>
              <tr class="total-row">${totalCells}</tr>
            </tfoot>
          </table>
          <div class="footer">
            <p>Total de registros: ${sellers.length}</p>
          </div>
        </body>
        </html>
      `;
    },

    exportHistoryToPDF() {
      if (!this.historyData || this.historyData.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const doc = new jsPDF("landscape", "mm", "a4");

        doc.setFontSize(16);
        doc.text("Relatório de Histórico Individual dos Vendedores", 14, 15);

        doc.setFontSize(10);
        doc.text(`Período: ${this.startDate} até ${this.endDate}`, 14, 25);
        const collectorsText = doc.splitTextToSize(
          `Cobradores: ${this.selectedCollectorsLabel()}`,
          260,
        );
        doc.text(collectorsText, 14, 30);
        const afterCollectorsY = 30 + Math.max(0, collectorsText.length - 1) * 4;
        doc.text(
          `Data de geração: ${jsFunctions.setDateTimeDisplay(new Date().toISOString())}`,
          14,
          afterCollectorsY + 5,
        );

        const tableColumns = this.getHistoryExportHeaders();
        const tableData = this.historyData.map(seller =>
          this.getHistoryExportRow(seller, true),
        );

        autoTable(doc, {
          head: [tableColumns],
          body: tableData,
          foot: [this.getHistoryTotalExportRow(true)],
          startY: afterCollectorsY + 10,
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [229, 229, 229], textColor: [0, 0, 0] },
          footStyles: {
            fillColor: [232, 238, 247],
            textColor: [0, 0, 0],
            fontStyle: "bold",
          },
          alternateRowStyles: { fillColor: [249, 249, 249] },
          margin: { left: 14, right: 14 },
          tableWidth: "wrap",
          showFoot: "lastPage",
        });

        const fileName = `Historico_Cobrador_${this.startDate}_${this.endDate}.pdf`;
        doc.save(fileName);
        toast.success("PDF exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar PDF:", error);
        toast.error("Erro ao exportar PDF");
      }
    },

    exportHistoryToExcel() {
      if (!this.historyData || this.historyData.length === 0) {
        toast.warning("Não há dados para exportar");
        return;
      }

      try {
        const headers = this.getHistoryExportHeaders();
        const tableData = this.historyData.map(seller =>
          this.getHistoryExportRow(seller, false),
        );

        const wb = XLSX.utils.book_new();
        const allData = [
          ["Relatório de Histórico Individual dos Vendedores"],
          [""],
          ["Período:", `${this.startDate} até ${this.endDate}`],
          ["Cobradores:", this.selectedCollectorsLabel()],
          [
            "Data de geração:",
            jsFunctions.setDateTimeDisplay(new Date().toISOString()),
          ],
          [""],
          headers,
          ...tableData,
        ];

        const ws = XLSX.utils.aoa_to_sheet(allData);
        ws["!cols"] = headers.map(() => ({ wch: 15 }));
        XLSX.utils.book_append_sheet(wb, ws, "Histórico Cobrador");

        const fileName = `Historico_Cobrador_${this.startDate}_${this.endDate}.xlsx`;
        XLSX.writeFile(wb, fileName);
        toast.success("Excel exportado com sucesso!");
      } catch (error) {
        console.error("Erro ao exportar Excel:", error);
        toast.error("Erro ao exportar Excel");
      }
    },
  }
};
</script>

<style scoped>
/* Container principal */
.financial-transactions-container {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

/* Cabeçalho da página */
.page-header {
  margin-bottom: 2rem;
}

.page-title {
  color: var(--text-primary);
  font-size: 2rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  align-items: center;
  gap: 0.75rem;
}

.page-title i {
  color: var(--accent-color);
  font-size: 1.75rem;
}

.page-subtitle {
  color: var(--text-secondary);
  margin: 0;
  font-size: 1rem;
  font-weight: 400;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: var(--text-secondary);
  font-size: 1.1rem;
}

.loading-spinner i {
  font-size: 1.5rem;
  color: var(--accent-color);
}

/* Tab content */
.tab-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Cards */
.filters-card,
.results-card {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-bottom: 1.5rem;
}

.results-card {
  overflow: visible !important;
}

.card-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(135deg, var(--secondary-color), #f1f1f1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  position: relative;
  z-index: 5;
  overflow: visible !important;
}

[data-theme="dark"] .card-header {
  background: linear-gradient(135deg, var(--secondary-color), #17171a);
}

.card-title {
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.card-title i {
  color: var(--accent-color);
  font-size: 1.125rem;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  z-index: 6;
}

.table-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary, var(--card-background));
}

.btn-zoom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;
}

.btn-zoom:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.06);
}

.btn-zoom:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-label {
  min-width: 3rem;
  text-align: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  user-select: none;
}

.zoom-label:hover {
  color: var(--primary-color, #3b82f6);
}

.card-actions .btn-action {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: auto !important;
  height: auto !important;
  min-width: unset !important;
  min-height: 36px;
  padding: 0.5rem 1rem !important;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: white;
  white-space: nowrap;
  box-sizing: border-box;
}

.card-actions .btn-action i {
  font-size: 0.875rem;
  line-height: 1;
  flex-shrink: 0;
}

.card-actions .btn-action:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.btn-print {
  background-color: #6b7280;
}

.btn-print:hover {
  background-color: #4b5563;
}

.btn-pdf {
  background-color: #dc2626;
}

.btn-pdf:hover {
  background-color: #b91c1c;
}

.btn-excel {
  background-color: #16a34a;
}

.btn-excel:hover {
  background-color: #15803d;
}

.columns-dropdown {
  position: relative;
  z-index: 50;
  flex: 0 0 auto;
}

.btn-columns {
  background-color: #2563eb;
}

.btn-columns:hover {
  background-color: #1d4ed8;
}

.columns-caret {
  font-size: 0.75rem;
  margin-left: 0.15rem;
}

.columns-menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  right: 0;
  z-index: 1000;
  min-width: 220px;
  max-height: 360px;
  overflow-y: auto;
  background: var(--card-background, #fff);
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 0.5rem;
}

[data-theme="dark"] .columns-menu {
  background: #1f1f1f;
  border-color: #3f3f46;
}

.columns-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.35rem 0.5rem 0.6rem;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  margin-bottom: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-primary, #111);
}

.columns-reset {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.columns-reset:hover {
  text-decoration: underline;
}

.columns-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--text-primary, #111);
  user-select: none;
}

.columns-menu-item:hover {
  background: rgba(37, 99, 235, 0.08);
}

.columns-menu-item.locked {
  opacity: 0.7;
  cursor: default;
}

.columns-menu-item input {
  width: 15px;
  height: 15px;
  accent-color: #2563eb;
}

@media (max-width: 768px) {
  .card-actions {
    width: 100%;
  }

  .columns-dropdown {
    flex: 1 1 auto;
  }

  .columns-dropdown .btn-action {
    width: 100% !important;
  }

  .columns-menu {
    left: 0;
    right: 0;
    min-width: 0;
  }
}

/* Filtros */
.filters-content {
  padding: 1.5rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.input-label i {
  color: var(--accent-color);
  font-size: 0.9rem;
}

.form-input {
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-color);
  background: var(--input-background);
  color: var(--text-primary);
  font-size: 0.95rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  width: 100%;
}

.form-input:focus {
  outline: none;
  border-color: var(--accent-color);
  box-shadow: 0 0 0 0.2rem var(--shadow);
}

.form-input::placeholder {
  color: var(--text-muted);
}

/* MultiSelect container */
.multiselect-container {
  position: relative;
  z-index: 1;
}

.filters-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color);
}

/* Buttons */
.btn-primary {
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  background: var(--accent-color);
  color: white;
  box-shadow: 0 3px 8px rgba(74, 144, 226, 0.3);
}

.btn-primary:hover:not(:disabled) {
  background: var(--accent-hover);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* IMPORTANTE: Garantir que os dropdowns dos MultiSelect sobreponham todos os elementos */
/* Remover overflow: hidden dos containers para permitir que o dropdown apareça */
.entries-container .filters-card {
  overflow: visible !important;
}

.filters-content {
  overflow: visible !important;
}

.filters-grid {
  overflow: visible !important;
}

.input-group {
  overflow: visible !important;
  position: relative !important;
  z-index: 1 !important;
}

.multiselect-container {
  position: relative !important;
  z-index: 10 !important;
}

/* Quando o multiselect estiver ativo/aberto, aumentar o z-index */
.multiselect-container:has(.multiselect--active) {
  z-index: 1000 !important;
}

/* Table */
.table-container {
  overflow-x: auto;
  max-width: 100%;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
  white-space: nowrap;
}

.modern-table th {
  background: #e5e5e5;
  color: var(--text-primary);
  padding: 1rem 1.5rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid var(--border-color);
  white-space: nowrap;
}

[data-theme="dark"] .modern-table th {
  background: #2d2d2d;
  color: #f4f4f5;
}

.modern-table td {
  padding: 0.75rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.data-row:nth-child(even) {
  background: rgba(0, 0, 0, 0.02);
}

[data-theme="dark"] .data-row:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.data-row:hover {
  background: rgba(96, 165, 250, 0.1);
}

/* Células da tabela */
.seller-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-start;
}

.seller-name {
  font-weight: 500;
  color: var(--text-primary);
}

.collector-name {
  font-weight: 500;
  color: var(--text-secondary);
}

.badge-inactive {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.currency-value {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: var(--text-primary);
}

.clickable-value {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  transition: all 0.2s ease;
}

.clickable-value:hover {
  opacity: 0.8;
  transform: scale(1.05);
  text-decoration-style: solid;
}

.clickable-value:active {
  transform: scale(0.98);
}

.currency-positive {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #10b981 !important;
}

.currency-negative {
  font-family: 'Consolas', 'Monaco', monospace;
  font-weight: 500;
  color: #ef4444 !important;
}

.final-balance {
  font-size: 1.05rem;
  background: rgba(96, 165, 250, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
}

/* Total row */
.total-row {
  background: rgba(96, 165, 250, 0.1);
  font-weight: 600;
}

.total-row td {
  border-top: 2px solid var(--accent-color);
  font-weight: 600;
}

.total-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  color: var(--accent-color);
  font-weight: 600;
}

/* Linha vazia na tabela */
.empty-row {
  background: transparent;
}

.empty-message {
  padding: 2rem !important;
  text-align: center;
}

.empty-table-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-secondary);
  font-size: 1rem;
}

.empty-table-message i {
  color: var(--accent-color);
  font-size: 1.25rem;
}

/* Células específicas da tabela de pagamentos */
.payment-date {
  font-size: 0.9rem;
  color: var(--text-primary);
}

.payment-period {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.total-amount {
  font-weight: 600;
  font-size: 1.05rem;
}

.paid-by {
  font-size: 0.9rem;
  color: var(--text-secondary);
}

.payment-notes {
  font-size: 0.85rem;
  color: var(--text-muted);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Estado vazio */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
}

.empty-state-content {
  text-align: center;
  color: var(--text-secondary);
}

.empty-state-content i {
  font-size: 4rem;
  color: var(--text-muted);
  margin-bottom: 1rem;
}

.empty-state-content h3 {
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
}

.empty-state-content p {
  margin: 0;
  font-size: 1rem;
  color: var(--text-secondary);
}

/* Estilo para Banca Recebeu/Pagou/Resultado */
.banca-header {
  padding: 0.5rem 1rem !important;
}

.banca-header-content {
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 2rem;
  width: 100%;
}

.banca-header-content span {
  flex: 1;
  text-align: center;
  font-weight: 600;
}

.banca-cell {
  padding: 0.5rem 1rem !important;
}

.banca-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.banca-values {
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: 2rem;
  width: 100%;
}

.banca-recebeu,
.banca-pagou {
  flex: 1;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.25rem;
}

.banca-resultado {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;
  padding: 0.375rem 0.75rem;
  background: rgba(96, 165, 250, 0.08);
  border-radius: 6px;
  border: 1px solid var(--border-color);
}

.resultado-icon {
  font-size: 1rem;
  color: var(--accent-color);
  margin-right: 0.25rem;
}

.resultado-value {
  font-weight: 600;
  font-size: 0.95rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .financial-transactions-container {
    padding: 1rem;
  }

  .page-title {
    font-size: 1.5rem;
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .modern-table {
    font-size: 0.8rem;
  }

  .modern-table th,
  .modern-table td {
    padding: 0.5rem 0.75rem;
  }
}

@media (max-width: 480px) {
  .modern-table th,
  .modern-table td {
    padding: 0.375rem 0.5rem;
  }
}
</style> 